import * as React from 'react'
import { useNotify, useTranslate } from 'react-admin'
import { useMutation } from 'react-query'
import * as yup from 'yup'
import { ErrorMessage, Form, Formik } from 'formik'
import { Link, makeStyles, Typography } from '@material-ui/core'
import { useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import ReCAPTCHA from 'react-google-recaptcha'
import MyTextField from '../../../components/form/fields/MyTextField'
import ButtonWithLoader from '../../../components/ui/ButtonWithLoader/ButtonWithLoader'
import MyTextFieldPassword from '../../../components/form/fields/MyTextFieldPassword'
import useHistoryPush from '../../../hooks/useHistoryPush'
import signUpFormStyles from './signUpForm.styles'
import SignUpFormAcceptTerms from '../SignUpFormAcceptTerms'
import MyBooleanField from '../../../components/form/fields/MyBooleanField'
import useFormValidation from '../../../hooks/useFormValidation'
import signUp from '../../../Apis/user/signUp'
import MySnackbar from '../../../components/MySnackbar/MySnackbar'
import getQueryParameterByName from '../../../utils/helpers/getQueryParameterByName'

const SignUpForm = () => {
  const reCaptchaRef = useRef()
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const translate = useTranslate()
  const { redirect } = useHistoryPush()
  const history = useHistory()
  const notify = useNotify()
  const { email, password, boolean } = useFormValidation()
  const useStyles = makeStyles((theme) => signUpFormStyles(theme))
  const classes = useStyles()

  // An invitation is matched by the address it was sent to. Arriving from an invitation's link the
  // address is locked: an account under any other one would never see the invitation.
  const invitedEmail = getQueryParameterByName('email') || ''
  const invitationToken = getQueryParameterByName('invitationToken')
  const isEmailLocked = !!invitationToken && !!invitedEmail

  const createAccount = useMutation((data) => signUp(data), {
    onSuccess: (response, values) => {
      reCaptchaRef.current.reset()

      // An invitation's link proves the address, so the account starts confirmed: log in and answer it.
      if (response?.data?.emailConfirmed) {
        notify(translate('ra.successMessages.accountCreatedLogIn'), 'info')
        history.push({
          pathname: '/login',
          search: `?email=${encodeURIComponent(values.email)}`,
          state: {
            nextPathname: '/invitationsList',
            nextSearch: `?invitation=${getQueryParameterByName('invitation') ?? ''}`,
          },
        })
        return
      }

      redirect('/send-confirmation-email', [{ initialEmail: values.email }])
    },
    onError: (data) => {
      reCaptchaRef.current.reset()
      setErrorMessage(data?.response?.data?.details ?? data?.response?.data?.message)
      setIsSnackbarOpen(true)
    },
  })

  const onCloseSnackbar = () => setIsSnackbarOpen(false)

  const onSubmit = async (data) => {
    const captchaToken = await reCaptchaRef.current.executeAsync()

    const { electronicMail, confirmTerms, ...rest } = data
    createAccount.mutate({
      ...rest,
      email: electronicMail,
      captchaToken,
      invitationToken,
    })
  }

  return (
    <Formik
      initialValues={{
        firstName: '',
        lastName: '',
        electronicMail: invitedEmail,
        password: '',
        acceptTerms: false,
        marketingConsent: false,
        dataSharingConsent: false,
      }}
      validationSchema={yup.object().shape({
        electronicMail: email,
        password,
        acceptTerms: boolean(translate('ra.validationErrors.terms')),
      })}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={(data) => onSubmit(data)}
    >
      {() => (
        <Form>
          <div className={classes.formWrapper}>
            <MyTextField label={translate('ra.formFields.firstName')} name='firstName' required />
            <MyTextField label={translate('ra.formFields.lastName')} name='lastName' required />
            <MyTextField
              label={translate('ra.formFields.email')}
              name='electronicMail'
              required
              disabled={isEmailLocked}
            />
            {isEmailLocked && (
              <Typography variant='body2' className={classes.lockedEmailNote}>
                {translate('ra.text.signUpInvitedEmailLocked')}
              </Typography>
            )}
            <MyTextFieldPassword
              label={translate('ra.formFields.password')}
              name='password'
              required
            />
            <div>
              <div className={classes.termWrapper}>
                <div className={classes.checkboxCell}>
                  <MyBooleanField name='acceptTerms' />
                </div>
                <div className={classes.termText}>
                  <SignUpFormAcceptTerms />
                </div>
              </div>
              <ErrorMessage component='div' name='acceptTerms' className={classes.errorMessage} />
            </div>
            <div className={classes.termWrapper}>
              <div className={classes.checkboxCell}>
                <MyBooleanField name='marketingConsent' />
              </div>
              <div className={classes.termText}>
                {translate('ra.formFields.marketingConsent')}
              </div>
            </div>
            <div className={classes.termWrapper}>
              <div className={classes.checkboxCell}>
                <MyBooleanField name='dataSharingConsent' />
              </div>
              <div className={classes.termText}>
                {translate('ra.formFields.dataSharingConsent')}
              </div>
            </div>
            <ButtonWithLoader
              fullWidth
              label={translate('ra.page.signUp')}
              style={{ marginTop: 10 }}
              loading={createAccount.isLoading}
            />
            <Link href='/login' variant='body2' className={classes.link}>
              {translate('ra.gridLinks.login')}
            </Link>
          </div>
          <ReCAPTCHA
            ref={reCaptchaRef}
            sitekey={process.env.REACT_APP_CAPTCHA_SITE_KEY}
            size='invisible'
          />
          <MySnackbar
            isOpen={isSnackbarOpen}
            onClose={onCloseSnackbar}
            errorMessage={errorMessage ?? translate('ra.errorMessages.signUpError')}
          />
        </Form>
      )}
    </Formik>
  )
}

export default SignUpForm
